/**
 * sl-728o.1.3: useTutorialTrigger Hook
 *
 * React hook that listens for action-driven triggers and calls onTriggered
 * when the user performs the expected action.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { TutorialTrigger } from '../types';

const DEFAULT_FALLBACK_TIMEOUT_MS = 30_000;

interface UseTutorialTriggerResult {
  /** Whether the fallback "Next" button should be shown */
  showFallbackButton: boolean;
}

/**
 * Listens for a trigger action and calls onTriggered when it fires.
 * Falls back to showing a "Next" button after timeout.
 */
export function useTutorialTrigger(
  trigger: TutorialTrigger | undefined,
  onTriggered: () => void,
  fallbackTimeoutMs?: number
): UseTutorialTriggerResult {
  const [showFallbackButton, setShowFallbackButton] = useState(false);
  const onTriggeredRef = useRef(onTriggered);
  onTriggeredRef.current = onTriggered;

  const fire = useCallback(() => {
    onTriggeredRef.current();
  }, []);

  // Reset fallback when trigger changes
  useEffect(() => {
    setShowFallbackButton(false);
  }, [trigger]);

  // Main trigger listener
  useEffect(() => {
    if (!trigger) return;

    // Manual trigger — show button immediately
    if (trigger.type === 'manual') {
      setShowFallbackButton(true);
      return;
    }

    let cleanup: (() => void) | undefined;

    switch (trigger.type) {
      case 'click': {
        if (!trigger.selector) break;
        const selector = trigger.selector;
        const handler = () => fire();
        // Use MutationObserver to wait for element if not yet in DOM
        const tryAttach = () => {
          const el = document.querySelector(selector);
          if (el) {
            el.addEventListener('click', handler);
            return () => el.removeEventListener('click', handler);
          }
          return undefined;
        };
        let detach = tryAttach();
        if (!detach) {
          // Element not yet in DOM — observe for it
          const observer = new MutationObserver(() => {
            if (!detach) {
              detach = tryAttach();
              if (detach) observer.disconnect();
            }
          });
          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });
          cleanup = () => {
            observer.disconnect();
            detach?.();
          };
        } else {
          cleanup = detach;
        }
        break;
      }

      case 'timeout': {
        const ms = trigger.timeoutMs ?? 5000;
        const timer = setTimeout(fire, ms);
        cleanup = () => clearTimeout(timer);
        break;
      }

      case 'custom': {
        if (!trigger.eventName) break;
        const eventName = trigger.eventName;
        const handler = () => fire();
        window.addEventListener(eventName, handler);
        cleanup = () => window.removeEventListener(eventName, handler);
        break;
      }

      case 'navigate': {
        const handler = () => fire();
        window.addEventListener('popstate', handler);
        cleanup = () => window.removeEventListener('popstate', handler);
        break;
      }

      case 'view-switch': {
        // Listen for the user clicking the mode-switch radio button.
        // The radio group contains buttons with data-testid like
        // "switch-edgeless-mode-button" / "switch-page-mode-button".
        // We attach a click listener to the target button directly.
        const targetMode = trigger.mode ?? 'edgeless';
        const targetTestId =
          targetMode === 'edgeless'
            ? 'switch-edgeless-mode-button'
            : 'switch-page-mode-button';
        const targetSelector = `[data-testid="${targetTestId}"]`;

        const handler = () => fire();

        // The button may not be in the DOM yet — use MutationObserver to wait for it
        const tryAttach = (): (() => void) | undefined => {
          const btn = document.querySelector(targetSelector);
          if (btn) {
            btn.addEventListener('click', handler);
            return () => btn.removeEventListener('click', handler);
          }
          return undefined;
        };

        let detach = tryAttach();
        if (!detach) {
          const observer = new MutationObserver(() => {
            if (!detach) {
              detach = tryAttach();
              if (detach) observer.disconnect();
            }
          });
          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });
          cleanup = () => {
            observer.disconnect();
            detach?.();
          };
        } else {
          cleanup = detach;
        }
        break;
      }

      case 'scroll': {
        // Listen for wheel events on the edgeless viewport
        const handler = (e: Event) => {
          const wheelEvent = e as WheelEvent;
          const dir = trigger.direction;
          if (
            (dir === 'down' && wheelEvent.deltaY > 0) ||
            (dir === 'up' && wheelEvent.deltaY < 0) ||
            (dir === 'right' && wheelEvent.deltaX > 0) ||
            (dir === 'left' && wheelEvent.deltaX < 0)
          ) {
            fire();
          }
        };
        document.addEventListener('wheel', handler, { passive: true });
        cleanup = () => document.removeEventListener('wheel', handler);
        break;
      }

      case 'select': {
        // Listen for selection changes — fire when user selects elements
        const handler = () => {
          const selection = document.getSelection();
          if (selection && !selection.isCollapsed) {
            fire();
          }
        };
        document.addEventListener('selectionchange', handler);
        // Also listen for BlockSuite element selections via click on canvas elements
        const clickHandler = (e: Event) => {
          const target = e.target as HTMLElement;
          if (target.closest('.affine-edgeless-note, affine-note')) {
            fire();
          }
        };
        document.addEventListener('click', clickHandler);
        cleanup = () => {
          document.removeEventListener('selectionchange', handler);
          document.removeEventListener('click', clickHandler);
        };
        break;
      }

      case 'sidebar-open': {
        // Watch for sidebar panel to appear
        const panelSelector = trigger.panel
          ? `[data-testid="${trigger.panel}"]`
          : '[data-testid="app-sidebar"]';
        const observer = new MutationObserver(() => {
          if (document.querySelector(panelSelector)) {
            observer.disconnect();
            fire();
          }
        });
        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
        cleanup = () => observer.disconnect();
        break;
      }
    }

    return () => {
      cleanup?.();
    };
  }, [trigger, fire]);

  // Fallback timer — shows button if trigger doesn't fire in time
  useEffect(() => {
    if (!trigger || trigger.type === 'manual') return;

    const timeout = fallbackTimeoutMs ?? DEFAULT_FALLBACK_TIMEOUT_MS;
    if (timeout <= 0) return;

    const timer = setTimeout(() => {
      setShowFallbackButton(true);
    }, timeout);

    return () => clearTimeout(timer);
  }, [trigger, fallbackTimeoutMs]);

  return { showFallbackButton };
}
